/**
 * Firebase Presence Core module.
 * Contains shared functionality for presence management across platforms.
 *
 * @module firebase/presence/core
 */

import { ref, get, update, serverTimestamp } from "firebase/database";
import { db } from "../config";

/**
 * Calculates the global status of an admin based on all their active sessions
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @returns {Promise<string>} Global status: 'online', 'away', or 'offline'
 */
export const calculateGlobalStatus = async (adminId) => {
  try {
    const webSessionsRef = ref(db, `admins/${adminId}/sessions/web`);
    const webSessionsSnapshot = await get(webSessionsRef);
    const webSessions = webSessionsSnapshot.val() || {};

    const mobileSessionsRef = ref(db, `admins/${adminId}/sessions/mobile`);
    const mobileSessionsSnapshot = await get(mobileSessionsRef);
    const mobileSessions = mobileSessionsSnapshot.val() || {};

    const allSessions = {
      ...webSessions,
      ...mobileSessions,
    };

    if (Object.keys(allSessions).length === 0) {
      return "offline";
    }

    if (
      Object.values(allSessions).some((session) => session.status === "online")
    ) {
      return "online";
    } else if (
      Object.values(allSessions).some((session) => session.status === "away")
    ) {
      return "away";
    }

    return "offline";
  } catch (error) {
    console.error("Error calculating global status:", error);
    return "offline";
  }
};

/**
 * Updates the global status of an admin in the database
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @returns {Promise<void>}
 */
export const recalculateAdminGlobalStatus = async (adminId) => {
  try {
    const globalStatus = await calculateGlobalStatus(adminId);

    await update(ref(db, `admins/${adminId}`), {
      globalStatus,
      lastUpdate: serverTimestamp(),
    });

    const activeAdminsRef = ref(db, "globalPresence/adminIds");
    if (globalStatus !== "offline") {
      await update(activeAdminsRef, { [adminId]: true });
    } else {
      await update(activeAdminsRef, { [adminId]: null });
    }

    const adminStatusRef = ref(db, "adminStatus");
    if (globalStatus === "online" || globalStatus === "away") {
      await update(adminStatusRef, { status: true });
    } else {
      await update(adminStatusRef, { status: false });
    }
  } catch (error) {
    console.error("Error updating admin global status:", error);
  }
};
